import { __awaiter } from "tslib";
import { useMessage } from '@/const/messages';
import { useStore } from '@/store/store';
import Chart from 'chart.js/auto';
import { ref } from 'vue';
import { useCurrencyPair } from './currency_pair';
/**
 * @summary 資産グラフを表示するための処理
 */
export const useBalancesGraph = () => {
    // consts
    const backgroundColors = [
        'rgba(255, 99, 132, 0.9)',
        'rgba(54, 162, 235, 0.9)',
        'rgba(255, 206, 86, 0.9)',
        'rgba(75, 192, 192, 0.9)',
        'rgba(153, 102, 255, 0.9)',
        'rgba(255, 159, 64, 0.9)',
    ];
    const borderColors = [
        'rgba(255, 99, 132, 0.9)',
        'rgba(54, 162, 235, 0.9)',
        'rgba(255, 206, 86, 0.9)',
        'rgba(75, 192, 192, 0.9)',
        'rgba(153, 102, 255, 0.9)',
        'rgba(255, 159, 64, 0.9)',
    ];
    // status
    const isLoading = ref(false);
    const isShowAllData = ref(false);
    const total = ref(0);
    const baseCurrency = ref('USD');
    const chart = ref();
    const showCurrency = ['BTC', 'ETH'];
    const store = useStore();
    const { getDollerRates, getCryptoRates } = useCurrencyPair();
    const { format, BALANCES_TITLES } = useMessage();
    /**
     * 換算する通貨を変更する処理
     * @param currency 変更先通貨
     */
    const changeBase = (currency) => {
        if (isLoading.value) {
            return;
        }
        baseCurrency.value = currency;
    };
    /**
     * 全通貨をグラフ表示するかの切り替え
     */
    const changeShowAll = () => {
        if (isLoading.value) {
            return;
        }
        isShowAllData.value = !isShowAllData.value;
    };
    /**
     * グラフの作成処理
     * @param labels 通貨の名称一覧
     * @param datasets 通貨のデータセット
     */
    const createChart = (labels, datasets) => {
        if (chart.value) {
            chart.value.destroy();
        }
        const ctx = document.getElementById('allocation-chart');
        chart.value = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels,
                datasets,
            },
            options: {
                plugins: {
                    legend: {
                        position: 'right',
                        align: 'center',
                        labels: {
                            color: store.state.theme === 'Dark' ? 'rgb(184, 188, 196)' : 'rgb(71, 77, 87)',
                        },
                    },
                },
            },
        });
    };
    /**
     * @returns グラフの再構成処理
     */
    const refresh = () => __awaiter(void 0, void 0, void 0, function* () {
        if (isLoading.value || store.state.balances.length === 0) {
            return;
        }
        isLoading.value = true;
        // レート再取得
        const [dollerRates, cryptoRates] = yield Promise.all([getDollerRates(), getCryptoRates()]).catch(() => []);
        // 再計算
        const { total: sum, labels, datasets, } = getCulculatedData(store.state.balances, dollerRates, cryptoRates, isShowAllData.value);
        total.value = sum;
        createChart(labels, datasets);
        isLoading.value = false;
    });
    const getCulculatedData = (balances, dollerRates, cryptoRates, isShowAllData) => {
        var _a, _b;
        // CRYPTO対USDTレート
        const CRYPTO_USDT = cryptoRates.filter((r) => r.symbol.toUpperCase().includes('USDT'));
        // CRYPTO対JPYレート
        const CRYPTO_JPY = cryptoRates.filter((r) => r.symbol.toUpperCase().includes('JPY'));
        // CRYPTO対USDレート
        const CRYPTO_USD = cryptoRates.filter((r) => r.symbol.toUpperCase().includes('USD') && !r.symbol.toUpperCase().includes('USDT'));
        // USDT/USDとUSD/JPYの間
        const USDT_USD = ((_a = dollerRates.find((rate) => rate.pair.toUpperCase() === 'USDTUSD')) === null || _a === void 0 ? void 0 : _a.mean) || 0;
        const USD_JPY = ((_b = dollerRates.find((rate) => rate.pair.toUpperCase() === 'USDJPY')) === null || _b === void 0 ? void 0 : _b.mean) || 0;
        // 資産情報のサマリー
        const usdtSummary = balances
            .map((balance) => {
            if (balance.symbol.toUpperCase() === 'USDT') {
                return { symbol: balance.symbol, usdtRate: Number(balance.balance) };
            }
            if (balance.symbol.toUpperCase() === 'JPY') {
                return { symbol: balance.symbol, usdtRate: Number(balance.balance) / USD_JPY / USDT_USD };
            }
            if (balance.symbol.toUpperCase() === 'USD') {
                return { symbol: balance.symbol, usdtRate: Number(balance.balance) / USDT_USD };
            }
            // 一旦全てUSDTレートに変換
            // USDレートがあるなら優先してレート換算する
            const usdRate = CRYPTO_USD.find((rate) => rate.symbol.toUpperCase().includes(balance.symbol));
            if (usdRate) {
                const usd = usdRate.price * Number(balance.balance);
                return { symbol: balance.symbol, usdtRate: usd / USDT_USD };
            }
            // USDTレートがあるならそのまま使う
            const usdtRate = CRYPTO_USDT.find((rate) => rate.symbol.toUpperCase().includes(balance.symbol));
            if (usdtRate) {
                const usdt = usdtRate.price * Number(balance.balance);
                return { symbol: balance.symbol, usdtRate: usdt };
            }
            // USDTレートが無いなら、JPYレートがあるか確認し、USD/JPY / USDT/USDからUSDTレートに変換する
            const jpyRate = CRYPTO_JPY.find((rate) => rate.symbol.toUpperCase().includes(balance.symbol));
            if (jpyRate) {
                const jpy = jpyRate.price * Number(balance.balance);
                const exchangeUSDT = jpy / USD_JPY / USDT_USD;
                return { symbol: balance.symbol, usdtRate: exchangeUSDT };
            }
            // レート情報が存在しないなら0として計算する
            return { symbol: balance.symbol, usdtRate: 0 };
        })
            .slice()
            .sort((a, b) => b.usdtRate - a.usdtRate);
        // 指定通貨とその他に変換
        let displayData = [...usdtSummary];
        if (!isShowAllData) {
            const specify = usdtSummary.filter((u) => showCurrency.includes(u.symbol.toUpperCase()));
            const othersTotal = usdtSummary
                .filter((u) => !showCurrency.includes(u.symbol.toUpperCase()))
                .reduce((b, c) => b + c.usdtRate, 0);
            displayData = [
                ...specify,
                {
                    symbol: format(BALANCES_TITLES.ALLOCATION_OTHERS),
                    usdtRate: othersTotal,
                },
            ];
        }
        // 対象のレートに変換 + 色の指定
        const exchanged = displayData.map((u, index) => {
            const colorIndex = 5 - (index % 6);
            const isUSD = baseCurrency.value === 'USD';
            const coff = isUSD ? USDT_USD : USDT_USD * USD_JPY;
            return {
                rate: u.usdtRate * coff,
                symbol: u.symbol,
                backgroundColor: backgroundColors[colorIndex],
                borderColor: borderColors[colorIndex],
            };
        });
        // 資産総額を計算
        const total = exchanged.map((e) => e.rate).reduce((b, c) => b + c, 0);
        // チャートに使用するデータ
        const labels = exchanged.map((balance) => {
            const percentage = (balance.rate / total) * 100 || 0;
            const adjustPer = percentage.toFixed(2);
            const zeroPad = adjustPer.length === 4 ? '  ' + adjustPer : adjustPer;
            return `${zeroPad}% ${balance.symbol}`;
        });
        const datasets = [
            {
                label: format(BALANCES_TITLES.ALLOCATION),
                data: exchanged.map((balance) => balance.rate),
                backgroundColor: exchanged.map((balance) => balance.backgroundColor),
                borderColor: exchanged.map((balance) => balance.borderColor),
                borderWidth: 1,
            },
        ];
        return { total, datasets, labels };
    };
    return { isLoading, isShowAllData, total, chart, baseCurrency, changeShowAll, changeBase, createChart, refresh };
};
